import {
	CheckCircleFilled,
	CloseCircleFilled,
	FormOutlined,
	LoadingOutlined,
	PlusOutlined,
	StarFilled,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Col, Row, Space, Spin } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import { tableColumnTextFilterConfig } from 'src/components/tableUtils';
import WeightChange from 'src/components/ui/weight-change';
import {
	MutationDeleteOffer,
	MutationUpdateOffer,
} from 'src/lib/gql/mutations';
import { QueryGetOffers } from 'src/lib/gql/queries';
import { OffersTable } from 'src/lib/interface';
import InfoTable from '../components/info-table';
import { ROUTES } from '../config/route';
import {
	CmsDeleteOfferCategoryMutation,
	CmsDeleteOfferMutationVariables,
	CmsGetPaginatedOffersQuery,
	CmsGetPaginatedOffersQueryVariables,
	CmsUpdateOfferCategoryMutation,
	CmsUpdateOfferMutationVariables,
} from 'src/lib/generated/_generated';

const Offers = () => {
	const [pageNumber, setPageNumber] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [offerName, setOfferName] = useState<string>('');
	const [offerVenueName, setOfferVenueName] = useState<string>('');
	const {
		data: qryData,
		loading,
		refetch: refetchQuery,
	} = useQuery<CmsGetPaginatedOffersQuery, CmsGetPaginatedOffersQueryVariables>(
		QueryGetOffers,
		{
			fetchPolicy: 'cache-and-network',
			variables: {
				pageNumber: pageNumber,
				perPage: perPage,
				name: offerName,
				venueName: offerVenueName,
				approved: true,
			},
		},
	);

	const [UpdateOffers] = useMutation<
		CmsUpdateOfferCategoryMutation,
		CmsUpdateOfferMutationVariables
	>(MutationUpdateOffer, {
		onError(error) {
			CustomNotification({
				pageName: 'Offers',
				pagePrefix: 'Offer',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const updateOffer = useCallback(
		async (
			id: string,
			enabled: boolean | undefined,
			pos: number | null | undefined,
			featured: boolean | undefined,
		) => {
			const rsp = await UpdateOffers({
				variables: {
					id: id,
					enabled: enabled,
					weight: pos,
					featured: featured,
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'Offers',
					pagePrefix: 'Offer',
					notificationType: 'updated',
				});

				await refetchQuery();
			}
		},
		[UpdateOffers, refetchQuery],
	);

	const [DeleteOffers] = useMutation<
		CmsDeleteOfferCategoryMutation,
		CmsDeleteOfferMutationVariables
	>(MutationDeleteOffer, {
		onError(error) {
			CustomNotification({
				pageName: 'Offers',
				pagePrefix: 'Offer',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const deleteOffer = async (id: string) => {
		if (!confirm('Confirm delete?')) return;
		const rsp = await DeleteOffers({
			variables: {
				id: id,
			},
		});

		if (rsp.data) {
			CustomNotification({
				pageName: 'Offers',
				pagePrefix: 'Offer',
				notificationType: 'deleted',
			});

			await refetchQuery();
		}
	};

	const p_Offers: OffersTable[] = [];

	const p_Offers_Query = qryData?.pagination?.offers?.result?.forEach(
		(offer, i) => {
			return p_Offers.push({
				id: offer?.id || '',
				key: (i + 1).toString() || '',
				enabled: offer?.enabled || false,
				featured: offer?.featured || false,
				name: offer?.title || '',
				offerDetails: offer?.details || '',
				offerDesc: offer?.description || '',
				venueName: offer?.venue?.name || '',
				weight: offer?.weight || 0,
				offerType: offer?.offerType?.name || '',
			});
		},
	);

	// p_Offers.sort((a, b) => {
	// 	if (a.weight > 0 || b.weight > 0) return a.weight - b.weight;
	// 	else return parseInt(a.key) - parseInt(b.key);
	// });

	// Fields
	const columns = [
		{
			title: 'ID',
			dataIndex: 'key',
			width: 50,
			align: 'center',
			render: (key: number) => {
				return key;
			},
		},
		{
			title: 'Position',
			width: 80,
			sorter: (a, b) => a.weight - b.weight,
			render: ({ id, weight }: { id: string; weight: number }) => {
				return (
					<WeightChange
						pos={weight}
						onSubmitChange={(weight) => {
							updateOffer(id, undefined, weight, undefined);
						}}
					/>
				);
			},
		},
		{
			title: 'Offer',
			dataIndex: 'name',
			key: 'title',
			width: 200,
			...tableColumnTextFilterConfig<OffersTable>(),
			onFilter: (value, record) => {
				return record.name
					.toString()
					.toLowerCase()
					.includes(value.toString().toLowerCase());
			},
		},
		{
			title: 'Offer Desc',
			dataIndex: 'offerDesc',
			key: 'offerDesc',
			width: 200,
			...tableColumnTextFilterConfig<OffersTable>(),
			onFilter: (value, record) => {
				return record.offerDesc
					.toString()
					.toLowerCase()
					.includes(value.toString().toLowerCase());
			},
		},
		{
			title: 'Venue Name',
			dataIndex: 'venueName',
			key: 'venueName',
			width: 150,
			align: 'center',
			...tableColumnTextFilterConfig<OffersTable>(),
			onFilter: (value, record) => {
				return record.venueName
					.toString()
					.toLowerCase()
					.includes(value.toString().toLowerCase());
			},
			// filters: qryData?.pagination?.offers?.result
			// 	?.map((offer) => {
			// 		return {
			// 			text: offer?.venue?.name,
			// 			value: offer?.venue?.name,
			// 		};
			// 	})
			// 	.filter(
			// 		(offer, index, self) =>
			// 			index ===
			// 			self.findIndex(
			// 				(o) => o.text === offer.text && o.value === offer.value,
			// 			),
			// 	),
			// onFilter: (value, record) => record.venueName.indexOf(value) === 0,
			// render: (name: string) => {
			// 	return name;
			// },
		},
		{
			title: 'Category',
			dataIndex: 'offerType',
			key: 'offerType',
			width: 150,
			align: 'center',
			filters: qryData?.pagination?.offers?.result
				?.map((offer) => {
					return {
						text: offer?.offerType?.name,
						value: offer?.offerType?.name,
					};
				})
				.filter(
					(offer, index, self) =>
						index ===
						self.findIndex(
							(o) => o.text === offer.text && o.value === offer.value,
						),
				),
			onFilter: (value, record) => record.offerType.indexOf(value) === 0,
			render: (name: string) => {
				return name;
			},
		},
		{
			title: 'Edit',
			key: 'enabled',
			width: 100,
			align: 'center',
			filters: [
				{
					text: 'Enabled',
					value: true,
				},
				{
					text: 'Disabled',
					value: false,
				},
				{
					text: 'Featured',
					value: 'Featured',
				},
			],
			filterMultiple: false,
			onFilter: (value, record) => {
				// console.log(value);
				if (value === 'Featured') return record.featured;
				else return record.enabled === value;
			},
			render: (props: { enabled: boolean; id: string; featured: boolean }) => {
				const { enabled, id, featured } = props;
				return (
					<Space>
						<StarFilled
							onClick={() => {
								updateOffer(id, undefined, undefined, !featured);
							}}
							style={{
								color: featured ? 'var(--highlighted)' : 'var(--inactive)',
								width: '12px',
								height: '12px',
							}}
							title="Featured?"
						/>
						<Link to={`${ROUTES.EDIT_OFFERS.path}/${id}`}>
							<FormOutlined
								style={{
									color: 'black',
									width: '12px',
									height: '12px',
								}}
							/>
						</Link>
						<CheckCircleFilled
							onClick={() => {
								updateOffer(id, !enabled, undefined, undefined);
							}}
							style={{
								color: enabled ? 'var(--valid)' : 'var(--inactive)',
								width: '12px',
								height: '12px',
							}}
						/>
						<CloseCircleFilled
							onClick={() => {
								deleteOffer(id);
							}}
							style={{
								color: 'var(--invalid)',
								width: '12px',
								height: '12px',
							}}
						/>
					</Space>
				);
			},
		},
	];

	return (
		<Row>
			<Col span={24}>
				<Text
					className="sub-header"
					style={{
						fontSize: 'clamp(16px, 1vw, 1.1vw)',
					}}
				>
					Offers
				</Text>
			</Col>

			<Col span={24}>
				<Row
					gutter={16}
					style={{
						margin: '16px 0',
					}}
					align={'bottom'}
				>
					<Link to={ROUTES.CREATE_OFFERS.path}>
						<Button className="btn-main">
							Create New Offer
							<PlusOutlined
								style={{
									position: 'relative',
									top: '1.5px',
								}}
							/>
						</Button>
					</Link>
				</Row>

				<Spin indicator={<LoadingOutlined />} spinning={loading}>
					<InfoTable
						data={p_Offers}
						columnData={columns}
						onChange={(pagination, filters, sorter) => {
							setPerPage(pagination?.pageSize);
							setPageNumber(pagination?.current);

							setOfferName(filters?.title?.toString().toLowerCase() || '');
							setOfferVenueName(
								filters?.venueName?.toString().toLowerCase() || '',
							);
						}}
						pagination={{
							total:
								qryData?.pagination?.offers?.paginationInfo?.totalResult || 0,
						}}
						other={{
							scroll: {
								x: 300,
							},
						}}
					/>
				</Spin>
			</Col>
		</Row>
	);
};

export default Offers;
